import { useMemo } from 'react';
import { TrendingDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { FeatureTrend } from '@/types/feedback';
import { halfSplitDelta, NEEDS_ATTENTION_DROP } from '@/lib/trend';

interface Dropped {
  feature: string;
  drop: number; // positive number of stars lost
  volume: number;
  rating: number;
}

/**
 * Ranked list of features whose average rating fell by at least `threshold`
 * stars over the period (first half vs second half), ordered by affected volume.
 */
export function NeedsAttention({
  features,
  threshold = NEEDS_ATTENTION_DROP,
  onFeatureSelect,
}: {
  features: FeatureTrend[];
  threshold?: number;
  onFeatureSelect?: (feature: string) => void;
}) {
  const dropped = useMemo<Dropped[]>(() => {
    return features
      .map((f) => {
        const { delta } = halfSplitDelta(f.daily);
        return delta === null
          ? null
          : { feature: f.stats.feature, drop: -delta, volume: f.stats.count, rating: f.stats.averageRating };
      })
      .filter((d): d is Dropped => d !== null && d.drop >= threshold)
      .sort((a, b) => b.volume - a.volume);
  }, [features, threshold]);

  return (
    <Card className={dropped.length > 0 ? 'border-warning-200' : ''}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <TrendingDown className="h-4 w-4 text-warning-500" />
          Needs attention
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Rating dropped ≥ {threshold.toFixed(1)}★ this period, by affected volume
        </p>
      </CardHeader>
      <CardContent className="space-y-1">
        {dropped.length === 0 ? (
          <p className="py-2 text-sm text-muted-foreground">
            No features declined by {threshold.toFixed(1)}★ or more. 🎉
          </p>
        ) : (
          dropped.map((d) => {
            const row = (
              <>
                <span className="truncate">{d.feature}</span>
                <span className="flex shrink-0 items-center gap-3">
                  <span className="text-muted-foreground">{d.volume}</span>
                  <span className="font-medium text-error-600">−{d.drop.toFixed(1)}★</span>
                </span>
              </>
            );
            return onFeatureSelect ? (
              <button
                key={d.feature}
                type="button"
                onClick={() => onFeatureSelect(d.feature)}
                title={`Filter feedback by "${d.feature}"`}
                className="-mx-2 flex w-full items-center justify-between rounded-md px-2 py-1 text-left text-sm transition-colors hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {row}
              </button>
            ) : (
              <div key={d.feature} className="flex items-center justify-between py-1 text-sm">
                {row}
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
